<template>
  <div class="model-layout" :class="isProcess ? 'process' : 'unprocess'">
    <div
      class="model-layout-left slide-left"
      v-show="leftWidth"
      :style="{ width: leftWidth + 'px' }"
    >
      <Box v-if="modelInfo">
        <template #title>
          {{ modelInfo.name }}
          <Tooltip
            class="item"
            effect="dark"
            :content="isPlaying ? '停止' : '朗读内容'"
            v-if="modelInfo.description"
            placement="bottom"
          >
            <Button
              type="text"
              :icon="
                isPlaying ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'
              "
              @click="onSpeech"
              style="font-size: 30px"
            ></Button>
          </Tooltip>
        </template>
        <p>{{ modelInfo.description || "-" }}</p>
      </Box>
      <Box title="实时数据" :height="isProcess ? 720 : 600">
        <div class="right-content">
          <div v-if="!sensorFilter.length" class="sensor-empty">暂无数据</div>
          <sz-sensors :sensors="sensorFilter" class="sensors"></sz-sensors>
        </div>
      </Box>
    </div>
    <div
      class="model-layout-right slide-right"
      v-show="rightWidth"
      :style="{ width: rightWidth + 'px' }"
    >
      <Box title="设备列表" :height="isProcess ? 900 : 750">
        <device-table :table-data="deviceFilter"></device-table>
      </Box>
    </div>
    <div
      class="model-layout-top slide-top"
      :style="{ left: leftWidth + 60 + 'px', right: rightWidth + 60 + 'px' }"
    >
      <slot name="top"></slot>
    </div>
    <div
      class="model-layout-bottom slide-bottom"
      :style="{ left: leftWidth + 60 + 'px', right: rightWidth + 60 + 'px' }"
    >
      <slot name="bottom"></slot>
    </div>
  </div>
</template>

<script setup>
import DeviceTable from "@/components/model/modules/device-table.vue";
import Box from "@/components/box/Box.vue";
import { computed, onMounted, ref, watch } from "vue";
import szSensors from "@/components/models/components/szSensors.vue";
import router from "@/router";
import store from "@/store";
import { getStation } from "@/services/services";
import { useSpeechSynthesis } from "@vueuse/core";
import { Button, Tooltip } from "element-ui";

const props = defineProps({
  autoSize: { type: Boolean, default: false },
  modelInfo: { type: Object, default: null },
  isSpeech: { type: Boolean, default: false },
});

const scale = ref(1);
const isProcess = router.currentRoute.name === "水厂工艺";

const deviceFilter = computed(() => {
  if (props.modelInfo) {
    var devices = store.state.devices.filter((item) => {
      let name = item.attributes.o_workshop?.name || "";
      return (
        name == props.modelInfo.name ||
        (name.includes(props.modelInfo.name) &&
          name.includes(props.modelInfo.id))
      );
    });
    return devices;
  }
  return [];
});

const sensorFilter = computed(() => {
  if (props.modelInfo) {
    return deviceFilter.value.flatMap((item) => {
      return item.sensors.map((s) => {
        if (!s.name.includes(item.name)) {
          s.name = item.name + s.name;
        }
        return s;
      });
    });
  }
  return [];
});

const leftWidth = computed(() => {
  return props.modelInfo ? 520 : 0;
});

const rightWidth = computed(() => {
  return props.modelInfo ? 520 : 0;
});

const speechText = ref(
  props.modelInfo?.name + "。" + props.modelInfo?.description
);
const { isPlaying, stop, speak } = useSpeechSynthesis(speechText, {
  lang: "zh-CN",
});
const onSpeech = () => {
  if (isPlaying.value) {
    stop();
  } else {
    if (props.modelInfo.description) {
      speechText.value =
        props.modelInfo?.name + "。" + props.modelInfo?.description;
      speak();
    }
  }
};
watch(
  () => props.isSpeech,
  (val) => {
    if (val) {
      onSpeech();
    } else {
      stop();
    }
  }
);

onMounted(() => {
  if (router.currentRoute.name !== "水厂工艺") {
    getStation("66c611f87848f9000192fb31").then(({ station }) => {
      let devices =
        station.devices.map((item) => {
          if (item.name === "送水泵房") {
            let sensors = ["W11", "W10", "W36", "W12"];
            item.sensors = item.sensors.filter((i) => sensors.includes(i.unit));
          }
          return item;
        }) || [];
      store.commit("SetDevices", devices);
    });
  }
});
</script>

<style scoped lang="less">
.model-layout {
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 70px;
  height: calc(100% - 70px);
  pointer-events: none;

  &-left {
    pointer-events: auto;
    position: absolute;
    left: 0;
    top: -70px;
    bottom: 0;
    background: url("@/assets/images/left.png") no-repeat;
    background-size: 100% 100%;
  }

  &-right {
    pointer-events: auto;
    position: absolute;
    right: 0;
    top: -70px;
    bottom: 0;
    background: url("@/assets/images/right.png") no-repeat;
    background-size: 100% 100%;
  }

  &-top {
    top: 0;
    left: 0;
    right: 0;
    pointer-events: auto;
    position: absolute;
  }

  &-bottom {
    bottom: 250px;
    left: 0;
    right: 0;
    pointer-events: auto;
    position: absolute;
  }

  .right-content {
    width: 100%;
    height: 100%;
    overflow-y: auto;

    .sensor-empty {
      height: 90%;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #909399;
    }

    .sensors {
      width: 100%;
    }
  }
}

.unprocess {
  .model-layout-left {
    padding-top: 70px;
    padding-left: 25px;
  }

  .model-layout-right {
    padding-top: 70px;
    padding-right: 25px;
  }
}

.process {
  height: 100%;

  .model-layout-left,
  .model-layout-right {
    padding: 0;
  }
}

.unprocess,
.process {
  p {
    font-size: 16px;
    line-height: 1.8;
  }

  ::v-deep(.c-box) {
    &-header {
      height: 40px;

      .header-title {
        font-size: 18px;
      }
    }

    &-body {
      font-size: 14px;
    }

    .el-table {
      font-size: 14px;

      .cell {
        line-height: 14px;
      }
    }
  }
}
</style>
